import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { 
  CreditCard, 
  Lock, 
  Shield, 
  CheckCircle, 
  ArrowLeft, 
  Loader2,
  AlertCircle,
  Eye,
  EyeOff
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { apiService, useApi } from '../services/api'

const cardSchema = yup.object({
  cardNumber: yup
    .string()
    .matches(/^\d{4}\s\d{4}\s\d{4}\s\d{4}$/, 'Número do cartão inválido')
    .required('Número do cartão é obrigatório'),
  cardHolder: yup
    .string()
    .min(3, 'Nome deve ter pelo menos 3 caracteres')
    .required('Nome do titular é obrigatório'),
  expiryMonth: yup
    .number()
    .min(1, 'Mês inválido')
    .max(12, 'Mês inválido')
    .required('Mês é obrigatório'),
  expiryYear: yup
    .number()
    .min(new Date().getFullYear(), 'Ano deve ser futuro')
    .required('Ano é obrigatório'),
  cvv: yup
    .string()
    .matches(/^\d{3,4}$/, 'CVV deve ter 3 ou 4 dígitos')
    .required('CVV é obrigatório'),
})

interface CardFormData {
  cardNumber: string
  cardHolder: string
  expiryMonth: number
  expiryYear: number
  cvv: string
}

const CardPaymentPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { checkOrderStatus } = useApi()
  const [isLoading, setIsLoading] = useState(false)
  const [showCVV, setShowCVV] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false)
  const [confirmationResult, setConfirmationResult] = useState<any>(null)

  const planName = searchParams.get('plan_name') || 'Plano Selecionado'
  const price = searchParams.get('price') || '0.00'

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CardFormData>({
    resolver: yupResolver(cardSchema),
  })

  const watchedCardNumber = watch('cardNumber')

  // Formatar número do cartão
  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '')
    const matches = v.match(/\d{4,16}/g)
    const match = (matches && matches[0]) || ''
    const parts = []
    
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4))
    }
    
    if (parts.length) {
      return parts.join(' ')
    } else {
      return v
    }
  }

  // Detectar tipo do cartão
  const getCardType = (number: string) => {
    const cleanNumber = number.replace(/\s/g, '')
    if (/^4/.test(cleanNumber)) return 'visa'
    if (/^5[1-5]/.test(cleanNumber)) return 'mastercard'
    if (/^3[47]/.test(cleanNumber)) return 'amex'
    if (/^6/.test(cleanNumber)) return 'discover'
    return 'unknown'
  }

  const onSubmit = async (data: CardFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      // Primeiro, tokenizar o cartão com a Pagar.me
      const tokenResponse = await fetch('https://api.pagar.me/core/v5/tokens', {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(import.meta.env.VITE_PAGARME_API_KEY || '')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'card',
          card: {
            number: data.cardNumber.replace(/\s/g, ''),
            holder_name: data.cardHolder,
            exp_month: data.expiryMonth,
            exp_year: data.expiryYear,
            cvv: data.cvv
          }
        })
      })

      const tokenResult = await tokenResponse.json()

      if (!tokenResponse.ok) {
        throw new Error(tokenResult.errors?.[0]?.message || 'Erro ao tokenizar cartão')
      }

      // Agora criar assinatura com o token
      const plan = searchParams.get('plan');
      if (!plan) {
        throw new Error('Plano não especificado');
      }
      
      const requestBody = {
        plan_type: Number(plan),
        payment_method: 'credit_card' as const,
        card_number: data.cardNumber,
        card_holder_name: data.cardHolder,
        card_expiration_month: data.expiryMonth,
        card_expiration_year: data.expiryYear,
        card_cvv: data.cvv
      };
      
      const result = await apiService.createSubscription(requestBody);
      console.log(`🎯 [${new Date().toISOString()}] Resultado do processamento:`, result)
      
      if (result.success) {
        // Se o pagamento for bem-sucedido, confirmar via check-order
        if (result.payment_id) {
          setSuccess(true);  // Set success state here
          await confirmPaymentViaCheckOrder(result.payment_id);
        } else {
          throw new Error('ID do pagamento não recebido');
        }
      } else {
        throw new Error(result.message || 'Erro ao processar pagamento');
      }
      
    } catch (error) {
      console.error('Erro no pagamento:', error)
      setError(error instanceof Error ? error.message : 'Erro ao processar pagamento. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value)
    setValue('cardNumber', formatted)
  }

  const cardType = getCardType(watchedCardNumber || '')

  // Função para confirmar pagamento via check-order
  const confirmPaymentViaCheckOrder = async (orderId: string) => {
    try {
      setIsConfirmingPayment(true)
      console.log(`🔍 [${new Date().toISOString()}] CONFIRMANDO PAGAMENTO via check-order: ${orderId}`)
      
      const result = await checkOrderStatus(orderId)
      console.log(`✅ [${new Date().toISOString()}] Confirmação do check-order:`, result)
      
      setConfirmationResult(result)
      
      if (result.is_paid) {
        console.log(`🎉 [${new Date().toISOString()}] PAGAMENTO CONFIRMADO via check-order!`)
        
        if (result.subscription_updated) {
          console.log(`✅ [${new Date().toISOString()}] Subscription atualizada com sucesso!`)
        } else if (result.current_subscription?.active) {
          console.log(`ℹ️ [${new Date().toISOString()}] Subscription já estava ativa`)
        }
        
        // Redirecionar após 3 segundos de confirmação
        setTimeout(() => {
          console.log(`🚀 [${new Date().toISOString()}] Redirecionando para página de sucesso`)
          navigate('/pagamento/sucesso', { 
            state: { 
              planName, 
              price, 
              paymentMethod: 'cartao',
              subscriptionId: 'confirmed',
              status: 'paid',
              confirmationResult: result
            }
          })
        }, 3000)
        
        return true
      } else {
        console.log(`❌ [${new Date().toISOString()}] Pagamento NÃO confirmado via check-order: ${result.order_status}`)
        return false
      }
    } catch (error) {
      console.error(`❌ [${new Date().toISOString()}] Erro ao confirmar pagamento via check-order:`, error)
      return false
    } finally {
      setIsConfirmingPayment(false)
    }
  }

  // Chamar confirmação via check-order quando a tela de sucesso aparecer
  React.useEffect(() => {
    if (success && !confirmationResult) {
      // Tentar obter order_id do localStorage ou de alguma fonte
      const orderId = localStorage.getItem('last_order_id')
      if (orderId) {
        console.log(`🎬 [${new Date().toISOString()}] Tela de sucesso iniciada - confirmando pagamento via check-order`)
        confirmPaymentViaCheckOrder(orderId)
      } else {
        // Se não tiver order_id, aguardar um pouco e redirecionar
        setTimeout(() => {
          navigate('/pagamento/sucesso', { 
            state: { 
              planName, 
              price, 
              paymentMethod: 'cartao',
              status: 'paid'
            }
          })
        }, 2000)
      }
    }
  }, [success, confirmationResult])

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Pagamento Processado!</h1>
          
          {/* Status da confirmação */}
          <div className="mb-6">
            {isConfirmingPayment ? (
              <div className="flex items-center justify-center space-x-2 text-blue-600 mb-4">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm font-medium">Confirmando pagamento...</span>
              </div>
            ) : confirmationResult ? (
              <div className="text-center mb-4">
                {confirmationResult.subscription_updated ? (
                  <div className="text-green-600 text-sm font-medium">
                    ✅ Subscription ativada com sucesso!
                  </div>
                ) : confirmationResult.current_subscription?.active ? (
                  <div className="text-blue-600 text-sm font-medium">
                    ℹ️ Subscription já estava ativa
                  </div>
                ) : (
                  <div className="text-gray-600 text-sm font-medium">
                    ✅ Pagamento confirmado
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-500 text-sm mb-4">
                Aguardando confirmação...
              </div>
            )}
          </div>

          <p className="text-xl text-gray-600 mb-8">
            {confirmationResult ? 'Redirecionando para página de sucesso...' : 'Processando pagamento...'}
          </p>
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white shadow-sm border-b border-gray-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-gray-600 hover:text-primary-500 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar</span>
            </button>
            
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-semibold text-gray-900">Pagamento Seguro</span>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Formulário de Pagamento */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Pagamento com Cartão
                </h1>
                <p className="text-gray-600">
                  Complete seus dados para finalizar a assinatura
                </p>
              </div>

              {/* Informações do Plano */}
              <div className="bg-primary-50 rounded-xl p-4 mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-primary-900">{planName}</h3>
                    <p className="text-sm text-primary-700">Assinatura mensal</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary-900">R$ {price}</p>
                    <p className="text-sm text-primary-700">/mês</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Número do Cartão */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número do Cartão
                  </label>
                  <div className="relative">
                    <input
                      {...register('cardNumber')}
                      type="text"
                      placeholder="0000 0000 0000 0000"
                      maxLength={19}
                      onChange={handleCardNumberChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                    />
                    {cardType !== 'unknown' && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className={`w-8 h-6 bg-${cardType === 'visa' ? 'blue' : 'red'}-500 rounded`}></div>
                      </div>
                    )}
                  </div>
                  {errors.cardNumber && (
                    <p className="mt-2 text-sm text-red-600">{errors.cardNumber.message}</p>
                  )}
                </div>

                {/* Nome do Titular */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome do Titular
                  </label>
                  <input
                    {...register('cardHolder')}
                    type="text"
                    placeholder="Como está no cartão"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  />
                  {errors.cardHolder && (
                    <p className="mt-2 text-sm text-red-600">{errors.cardHolder.message}</p>
                  )}
                </div>

                {/* Data de Expiração e CVV */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mês
                    </label>
                    <select
                      {...register('expiryMonth')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="">Mês</option>
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {String(i + 1).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                    {errors.expiryMonth && (
                      <p className="mt-2 text-sm text-red-600">{errors.expiryMonth.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ano
                    </label>
                    <select
                      {...register('expiryYear')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="">Ano</option>
                      {Array.from({ length: 10 }, (_, i) => {
                        const year = new Date().getFullYear() + i
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        )
                      })}
                    </select>
                    {errors.expiryYear && (
                      <p className="mt-2 text-sm text-red-600">{errors.expiryYear.message}</p>
                    )}
                  </div>
                </div>

                {/* CVV */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CVV
                  </label>
                  <div className="relative">
                    <input
                      {...register('cvv')}
                      type={showCVV ? 'text' : 'password'}
                      placeholder="123"
                      maxLength={4}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCVV(!showCVV)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCVV ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.cvv && (
                    <p className="mt-2 text-sm text-red-600">{errors.cvv.message}</p>
                  )}
                </div>

                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3"
                  >
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-600">{error}</p>
                  </motion.div>
                )}

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-primary-500 to-accent-500 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Processando...</span>
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      <span>Finalizar Pagamento</span>
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </motion.div>

          {/* Informações de Segurança */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-6"
          >
            {/* Segurança */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Pagamento 100% Seguro</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Criptografia SSL</h4>
                    <p className="text-sm text-gray-600">Seus dados são protegidos com criptografia de ponta</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">PCI DSS Compliant</h4>
                    <p className="text-sm text-gray-600">Certificação de segurança para processamento de cartões</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Sem Armazenamento</h4>
                    <p className="text-sm text-gray-600">Não armazenamos dados sensíveis do cartão</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cartões Aceitos */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Cartões Aceitos</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-4 bg-blue-500 rounded"></div>
                  <span className="text-sm font-medium text-gray-700">Visa</span>
                </div>
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-4 bg-red-500 rounded"></div>
                  <span className="text-sm font-medium text-gray-700">Mastercard</span>
                </div>
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-4 bg-blue-600 rounded"></div>
                  <span className="text-sm font-medium text-gray-700">American Express</span>
                </div>
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-4 bg-orange-500 rounded"></div>
                  <span className="text-sm font-medium text-gray-700">Discover</span>
                </div>
              </div>
            </div>

            {/* Suporte */}
            <div className="bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl p-6 text-white">
              <h3 className="text-lg font-semibold mb-2">Precisa de Ajuda?</h3>
              <p className="text-primary-100 mb-4">
                Nossa equipe está disponível 24/7 para ajudar com qualquer dúvida
              </p>
              <button className="bg-white text-primary-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200">
                Falar com Suporte
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default CardPaymentPage
